<template>
  <v-card id="notification-campaign-list">
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <div class="d-flex align-center pb-5">
        <v-btn
          color="primary"
          class="me-3"
          :disabled="hasNotPermission('Permissions.PushNotificationCreate')"
          @click="add"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiPlus }}
          </v-icon>
          <span>Добавить</span>
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex align-center pb-5">
        <v-select
          v-model="filters.status"
          :items="statusItems"
          dense
          outlined
          hide-details
          label="Статус"
          class="notification-campaign-list-status me-3"
        ></v-select>
        <v-text-field
          v-model="filters.searchQuery"
          single-line
          dense
          outlined
          hide-details
          placeholder="Поиск..."
          class="notification-campaign-list-search me-3"
        ></v-text-field>
      </div>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="campaigns"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      disable-sort
      class="text-no-wrap"
    >
      <template #[`item.scheduledAt`]="{item}">
        {{ toDateTimeString(item.scheduledAt) }}
      </template>

      <template #[`item.channels`]="{item}">
        <v-chip
          v-if="item.toApp"
          small
          outlined
          class="me-1"
        >
          Приложение
        </v-chip>
        <v-chip
          v-if="item.toBrowser"
          small
          outlined
        >
          Браузер
        </v-chip>
      </template>

      <template #[`item.status`]="{item}">
        <v-chip
          small
          :color="statusColor(item.status)"
          text-color="white"
        >
          {{ item.statusName }}
        </v-chip>
      </template>

      <template #[`item.stats`]="{item}">
        <v-tooltip
          bottom
          :disabled="!channelsOf(item).length"
        >
          <template #activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
            >
              {{ total(item, 'sent') }} /
              <span :class="{ 'error--text': total(item, 'failed') }">{{ total(item, 'failed') }}</span>
            </span>
          </template>
          <div
            v-for="channel in channelsOf(item)"
            :key="channel.channel"
          >
            {{ channel.channelName }}: отправлено {{ channel.sent }} из {{ channel.total }},
            ошибок {{ channel.failed }}, ждут {{ channel.pending }},
            истёк срок {{ channel.expired }}, отменено {{ channel.cancelled }}
          </div>
        </v-tooltip>
      </template>

      <template #[`item.note`]="{item}">
        <div class="notification-campaign-list-note">
          {{ item.note }}
        </div>
      </template>

      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="!item.canEdit || hasNotPermission('Permissions.PushNotificationUpdate')"
                v-bind="attrs"
                v-on="on"
                @click="edit(item.id)"
              >
                <v-icon size="18">
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Изменить</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="!item.canSendNow || hasNotPermission('Permissions.PushNotificationSend')"
                v-bind="attrs"
                v-on="on"
                @click="sendNowPopupShow(item.id, item.name)"
              >
                <v-icon size="18">
                  {{ icons.mdiSend }}
                </v-icon>
              </v-btn>
            </template>
            <span>Разослать сейчас</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                :disabled="!item.canCancel || hasNotPermission('Permissions.PushNotificationDelete')"
                v-bind="attrs"
                v-on="on"
                @click="cancelPopupShow(item.id)"
              >
                <v-icon size="18">
                  {{ icons.mdiCancel }}
                </v-icon>
              </v-btn>
            </template>
            <span>Отменить</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :to="{ name: 'push-notification-history', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiAccountMultipleOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Получатели</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <notification-campaign-form
      :show="formShown"
      :label="formLabel"
      :save="saveCampaign"
      :cancellation="closeForm"
    />

    <delete-dialog
      ref="sendNowPopup"
      :label="sendNowLabel"
      @delete-confirmed="sendNow"
    />

    <delete-dialog
      ref="cancelPopup"
      label="Рассылка будет остановлена: что ещё не ушло, уже не уйдёт"
      @delete-confirmed="cancel"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiSend,
  mdiCancel,
  mdiAccountMultipleOutline,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import DeleteDialog from '@/components/delete-dialog'
import NotificationCampaignForm from '@/views/notification-campaign/notification-campaign-form.vue'
import { hasPermission, toDateTimeString } from '@/helpers'
import {
  campaignChannels,
  campaignStatusColor,
  campaignStatusItems,
  campaignTotal,
} from '@/helpers/notifications'

export default {
  name: 'NotificationCampaignList',
  components: { DeleteDialog, NotificationCampaignForm },
  data: () => ({
    icons: {
      mdiPlus,
      mdiPencilOutline,
      mdiSend,
      mdiCancel,
      mdiAccountMultipleOutline,
    },
    toDateTimeString,
    statusItems: campaignStatusItems,
    sendNowLabel: '',
    formShown: false,
    formLabel: '',
  }),
  computed: {
    ...mapFormHandlers('notificationCampaign', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.searchQuery',
      'filters.status',
    ]),
    ...mapState('notificationCampaign', {
      campaigns: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
    }),
    headers() {
      return [
        { text: 'Название', value: 'name' },
        { text: 'Заголовок', value: 'title' },
        { text: 'Время отправки', value: 'scheduledAt' },
        { text: 'Каналы', value: 'channels' },
        { text: 'Статус', value: 'status' },
        { text: 'Отправлено / ошибок', value: 'stats' },
        { text: 'Примечание', value: 'note' },
        { text: 'Действия', value: 'actions', align: 'center' },
      ]
    },
  },
  async created() {
    document.title = 'Рассылки'
    await this.fetchCampaigns()
  },
  methods: {
    ...mapActions('notificationCampaign', {
      fetchCampaigns: 'fetchList',
      cancel: 'cancel',
      sendNow: 'sendNow',
    }),
    ...mapActions('notificationCampaign/form', ['fetchById', 'resetForm', 'create', 'update']),
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    statusColor(status) {
      return campaignStatusColor(status)
    },
    channelsOf(campaign) {
      return campaignChannels(campaign)
    },
    total(campaign, field) {
      return campaignTotal(campaign, field)
    },
    add() {
      this.resetForm()
      this.formLabel = 'Новая рассылка'
      this.formShown = true
    },
    async edit(id) {
      await this.fetchById(id)
      this.formLabel = 'Рассылка'
      this.formShown = true
    },
    closeForm() {
      this.formShown = false
    },
    async saveCampaign() {
      const { id } = this.$store.state.notificationCampaign.form

      await (id ? this.update() : this.create())

      this.formShown = false
      this.fetchCampaigns()
    },
    sendNowPopupShow(id, name) {
      this.sendNowLabel = `Рассылка «${name}» уйдёт сейчас, не дожидаясь назначенного времени.`
      this.$refs.sendNowPopup.open(id)
    },
    cancelPopupShow(id) {
      this.$refs.cancelPopup.open(id)
    },
  },
}
</script>

<style lang="scss" scoped>
#notification-campaign-list {
  .notification-campaign-list-status {
    max-width: 14rem;
  }

  .notification-campaign-list-search {
    max-width: 10.625rem;
  }

  .notification-campaign-list-note {
    white-space: normal;
    min-width: 14rem;
  }
}
</style>
